// frontend/src/pages/MaintenanceLogPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Table from 'react-bootstrap/Table';
import Alert from 'react-bootstrap/Alert';
import { MaintenanceLogForm } from '../components/forms/MaintenanceLogForm'; // Importar el formulario

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Interfaz para el Log de Mantenimiento (ajusta según tu BD/API)
interface MaintenanceLog {
  id: number;
  asset_id: number; // Considera obtener el nombre/identificador del activo con un JOIN en backend o una segunda llamada
  log_date: string;
  maintenance_type: string;
  description: string;
  cost_parts: number;
  cost_labor: number;
  reported_by: string | null;
  // Podrías añadir campos de Asset aquí si el backend los incluye
  asset_name?: string;
  asset_identifier?: string;
}

export function MaintenanceLogPage() {
  const [logs, setLogs] = useState<MaintenanceLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Función para cargar los registros
  const fetchLogs = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
       // Pedimos los últimos 20 registros, por ejemplo
      const response = await fetch(`${API_BASE_URL}/api/data/maintenance-logs?limit=20&sort=desc`);
      if (!response.ok) {
        throw new Error('Error al obtener los registros de mantenimiento.');
      }
      const data: MaintenanceLog[] = await response.json();
      // Formatear fecha para legibilidad (opcional)
      const formattedData = data.map(log => ({
          ...log,
          log_date: new Date(log.log_date).toLocaleString() // Formato local
      }));
      setLogs(formattedData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
      console.error("Error fetching maintenance logs:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Cargar registros al montar
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  return (
    <Container fluid className="mt-3">
      <Row>
        {/* Columna para la tabla de registros */}
        <Col md={8} className="mb-3">
          <h3>Registros de Mantenimiento Recientes</h3>
          {isLoading && <p>Cargando registros...</p>}
          {error && <Alert variant="danger">{error}</Alert>}
          {!isLoading && !error && (
            <Table striped bordered hover responsive size="sm">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Fecha</th>
                  <th>Activo ID</th> {/* Idealmente mostrar Nombre/Identificador */}
                  <th>Tipo</th>
                  <th>Descripción</th>
                  <th>Costo Partes</th>
                  <th>Costo M. Obra</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {logs.length > 0 ? (
                  logs.map(log => (
                    <tr key={log.id}>
                      <td>{log.id}</td>
                      <td>{log.log_date}</td>
                      <td>{log.asset_id} {log.asset_name ? `(${log.asset_name})` : ''}</td> {/* Mostrar nombre si viene */}
                      <td>{log.maintenance_type}</td>
                      <td>{log.description}</td>
                      <td>${(log.cost_parts || 0).toFixed(2)}</td>
                      <td>${(log.cost_labor || 0).toFixed(2)}</td>
                       <td>${((log.cost_parts || 0) + (log.cost_labor || 0)).toFixed(2)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="text-center">No hay registros de mantenimiento.</td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </Col>

        {/* Columna para el formulario */}
        <Col md={4}>
          {/* Refrescar la lista cuando se crea un nuevo log */}
          <MaintenanceLogForm onLogCreated={fetchLogs} />
        </Col>
      </Row>
    </Container>
  );
}